import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Palmtree, ClipboardCheck, Receipt, LifeBuoy, UserPlus, FileWarning,
  type LucideIcon,
} from "lucide-react";

import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { SummaryStatCard } from "../../SummaryStatCard.js";
import type { FloodCardTone } from "../../ui/flood-card.js";
import type { WidgetProps } from "../widget-registry.js";
import { KpiSummarySkeleton } from "./widget-skeletons.js";

interface Summary {
  headcount: { active: number; onLeave: number };
  pendingApprovals: { leaves: number; expenses: number };
  openItTickets: number;
  onboardingInProgress: number;
  documentsExpiringWithin90Days: number;
}

interface StatTile {
  key: string;
  icon: LucideIcon;
  label: string;
  value: number;
  tone: FloodCardTone;
  note?: string;
  to?: string;
}

/** One summary tile; wrapped in a Link when the stat drills into a module. */
function StatCard({ icon, label, value, tone, to }: Omit<StatTile, "key">) {
  const tile = (
    // layout="row" is SummaryStatCard's own compact variant (the one the
    // attendance page uses): a 28px icon beside the label stacked over the value,
    // on sm padding. It keeps every tone, slot and flood of the tall tile at
    // roughly a quarter of the height.
    //
    // The caption ("requires action") is dropped here on purpose: in row layout
    // it is pinned to the right edge and does not shrink, so in a ~190px column
    // it pushed the value out. The label already says what the number is.
    <SummaryStatCard
      layout="row"
      icon={icon}
      tone={tone}
      label={label}
      value={value}
      className="h-full"
      card={{ orb: "medium", lift: true }}
    />
  );
  // min-w-0 lets a column shrink past the label's longest word; without it a grid
  // track refuses to go below min-content and the row overflows instead of fitting.
  return to
    ? <Link to={to} className="min-w-0 no-underline">{tile}</Link>
    : <div className="min-w-0">{tile}</div>;
}

/** The seven headline stats, each on its own tone so the row reads at a glance. */
function statTiles(summary: Summary): StatTile[] {
  return [
    { key: "active",     icon: Users,          label: "Active Employees",          value: summary.headcount.active,              tone: "brand",   to: "/employees" },
    { key: "onLeave",    icon: Palmtree,       label: "On Leave",                  value: summary.headcount.onLeave,             tone: "info",    to: "/leave" },
    { key: "leaveAppr",  icon: ClipboardCheck, label: "Pending Leave Approvals",   value: summary.pendingApprovals.leaves,       tone: "warning", note: "requires action", to: "/leave" },
    { key: "expAppr",    icon: Receipt,        label: "Pending Expense Approvals", value: summary.pendingApprovals.expenses,     tone: "rose",    note: "requires action", to: "/expenses" },
    { key: "tickets",    icon: LifeBuoy,       label: "Open IT Tickets",           value: summary.openItTickets,                 tone: "accent",  to: "/assets" },
    { key: "onboarding", icon: UserPlus,       label: "Onboarding In Progress",    value: summary.onboardingInProgress,          tone: "cyan",    to: "/onboarding" },
    { key: "docs",       icon: FileWarning,    label: "Docs Expiring ≤90 days",    value: summary.documentsExpiringWithin90Days, tone: "danger",  note: "review expiry" },
  ];
}

/**
 * The headline counts row. Owns its own fetch, so the dashboard page no longer
 * loads data on behalf of a widget it may not even be showing.
 */
export function KpiSummaryWidget(_props: WidgetProps) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get<Summary>("/reports/summary")
      .then((res) => { if (!cancelled) setSummary(res.data); })
      .catch((e: unknown) => { if (!cancelled) logError("KpiSummaryWidget.load", e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <KpiSummarySkeleton />;
  if (!summary) return null;

  // Nested grid: the widget occupies its span on the dashboard grid, and lays its
  // own seven tiles out inside that, down to two columns on a phone.
  return (
    <div className="grid grid-cols-2 items-stretch gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
      {statTiles(summary).map(({ key, ...tile }) => <StatCard key={key} {...tile} />)}
    </div>
  );
}
